import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type {
  OrdemServicoAtividade,
  OrdemServicoAtividadeUpdate,
  PaginatedResponse,
} from '../types'

/** Query key da lista de atividades de uma OS — reutilizada na invalidação. */
function atividadesKey(osId: string) {
  return ['ordens-servico', osId, 'atividades'] as const
}

/**
 * Atividades (checklist materializado) de uma OS. Leitura paginada; o backend
 * ordena por `criado_em`. A executora só enxerga OS atribuídas a si (404 caso
 * contrário).
 */
export function useOSAtividades(osId: string) {
  return useQuery({
    queryKey: atividadesKey(osId),
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<OrdemServicoAtividade>>(
        `/ordens-servico/${osId}/atividades/`,
      )
      return r.data
    },
    enabled: !!osId,
    staleTime: 1000 * 30,
  })
}

interface AtividadeUpdateArgs {
  atividadeId: string
  data: OrdemServicoAtividadeUpdate
}

/**
 * PATCH de uma atividade da OS. Aceita qualquer subconjunto de
 * { executada, data_execucao, medicao_valor, observacao }. Marcar
 * `executada: true` sem `data_execucao` faz o servidor assumir hoje; o backend
 * responde 400 se a OS estiver aprovada/cancelada. Invalida a lista no sucesso.
 */
export function useOSAtividadeUpdate(osId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ atividadeId, data }: AtividadeUpdateArgs) => {
      const r = await api.patch<OrdemServicoAtividade>(
        `/ordens-servico/${osId}/atividades/${atividadeId}/`,
        data,
      )
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: atividadesKey(osId) }),
  })
}
